<script setup lang="ts">
import { mdiClose } from "@mdi/js";
import { DateTime as Luxon } from "luxon";
import FormDate from "~/components/ui/Form/Date.vue";
import t from "~/utils/translate";

interface Props {
	filter: {
		toStartEpoch: number | undefined;
		fromStartEpoch: number | undefined;
	};
}

defineProps<Props>();
const emits = defineEmits(["update-from-epoch", "update-to-epoch"]);

const active = ref("");
const dateButton = tw`flex w-full justify-center items-center font-bold rounded  h-7 cursor-pointer select-none`;
const activeButton = tw`bg-identity text-light-50`;

const handleReset = () => {
	active.value = "";
	emits("update-from-epoch", undefined);
	emits("update-to-epoch", undefined);
};

const handleSelect = (key: string) => {
	if (key === active.value) {
		active.value = "";
		handleReset();
	} else {
		if (key === "today") {
			emits("update-from-epoch", Luxon.now().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toSeconds());
			emits("update-to-epoch", Luxon.now().set({ hour: 23, minute: 59, second: 59, millisecond: 0 }).toSeconds());
		} else if (key === "tomorrow") {
			emits("update-from-epoch", Luxon.now().plus({ days: 1 }).set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toSeconds());
			emits("update-to-epoch", Luxon.now().plus({ days: 1 }).set({ hour: 23, minute: 59, second: 59, millisecond: 0 }).toSeconds());
		} else if (key === "weekend") {
			emits("update-from-epoch", Luxon.now().set({ weekday: 6, hour: 0, minute: 0, second: 0, millisecond: 0 }).toSeconds());
			emits("update-to-epoch", Luxon.now().set({ weekday: 6, hour: 23, minute: 59, second: 59, millisecond: 0 }).plus({ days: 1 }).toSeconds());
		}
		active.value = key;
	}
};

const startDate = ref(new Date());
</script>

<template>
	<div class="flex flex-col gap-6 p-2 text-xs">
		<div class="flex flex-col gap-2">
			<strong>{{ t("show-games-for") }}</strong>
			<div
				:class="[dateButton, active === 'today' ? activeButton : ' bg-light-50 text-dark-600 dark:bg-dark-600 dark:text-light-50']"
				@click="handleSelect('today')"
			>
				{{ t("today") }}
			</div>
			<div
				:class="[dateButton, active === 'tomorrow' ? activeButton : 'bg-light-50 text-dark-600 dark:bg-dark-600 dark:text-light-50']"
				@click="handleSelect('tomorrow')"
			>
				{{ t("tomorrow") }}
			</div>
			<div
				:class="[dateButton, active === 'weekend' ? activeButton : 'bg-light-50 text-dark-600 dark:bg-dark-600 dark:text-light-50']"
				@click="handleSelect('weekend')"
			>
				{{ t("weekend") }}
			</div>
		</div>
		<div class="flex flex-col gap-2">
			<strong>{{ t("or-between") }}</strong>
			<div class="grid w-full h-8 grid-cols-2 border rounded bg-light-50 border-light-400 dark:bg-dark-700 dark:border-dark-500">
				<div class="relative flex items-center justify-center overflow-hidden border-r border-r-light-400 dark:border-r-dark-500">
					<FormDate
						class="absolute left-0 w-full h-full -translate-y-1/2 opacity-0 top-1/2"
						id="from-start-date"
						no-floating-label
						label=""
						:model-value="''"
						:min-date="new Date()"
						@update:model-value="
							(date) => {
								startDate = date ? new Date(date) : new Date();
								emits('update-from-epoch', Luxon.fromISO(date).toSeconds() || undefined);
							}
						"
					/>
					<span v-if="filter.fromStartEpoch">{{ Luxon.fromSeconds(filter.fromStartEpoch).toFormat("ccc, dd LLL") }}</span>
					<span v-else>{{ t("start-date") }}</span>
				</div>
				<div class="relative flex items-center justify-center">
					<FormDate
						class="absolute left-0 w-full h-full -translate-y-1/2 opacity-0 top-1/2"
						id="to-start-date"
						no-floating-label
						label=""
						:model-value="''"
						:min-date="startDate"
						@update:model-value="
							(date) => {
								emits('update-to-epoch', Luxon.fromISO(date).set({ hour: 23, minute: 59, second: 59, millisecond: 0 }).toSeconds() || undefined);
							}
						"
					/>
					<span v-if="filter.toStartEpoch">{{ Luxon.fromSeconds(filter.toStartEpoch).toFormat("ccc, dd LLL") }}</span>
					<span v-else>{{ t("end-date") }}</span>
				</div>
			</div>
			<div>
				<UiButton
					size="small"
					v-if="filter.fromStartEpoch || filter.toStartEpoch"
					variant="tertiary"
					:icon="mdiClose"
					@click="handleReset"
					:label="t('reset')"
				/>
			</div>
		</div>
	</div>
</template>
